// Billed / Unbilled Time by Matter — the one module every consumer reads.
//
// Feedback #837 (2026-10-01).  The two matter reports on the Reports page used
// to compute their rows, totals and labels inline, once per output: the
// screen table, the PDF, the Excel export and print each had their own copy.
// Four copies of the same arithmetic is how a printed total ends up disagreeing
// with the screen it was printed from.  Everything that decides WHAT a row
// says or WHAT a total is lives here, and the four outputs only choose HOW to
// draw it (formatCell / footerCells take a `target`).
//
// Pure: no React, no api, no jsPDF, no Date.now().  Money is integer CENTS and
// hours are integer TENTHS from the moment a row is built, so every printed
// total is exactly the sum of the printed rows — no float drift between a
// footer and the column above it.
//
// California compliance notes (Cal. Rules of Prof. Conduct 1.4 / 1.5,
// Bus. & Prof. Code §§ 6147-6148):
//   • "Billable value" counts ONLY entries marked Billable.  No-charge and
//     non-billable time never contributes to it, on any footer line.
//   • "Standard value" (hours × rate on EVERY row) is the lodestar figure for
//     fee motions.  Every output carries a note saying it is a measure of the
//     work recorded, not an amount billed or owed, so a printed report cannot
//     be read as a bill for no-charge time.
//   • On the Billed report, "Billed" is what was actually CHARGED: a line on
//     an Omnia invoice with billing status Billable.  Time billed at no
//     charge (including Amicus $0 bills) prints $0.00 — never its face value.

import { MATTER_FULL_HISTORY_REPORTS } from './reportTimeEntriesUrl'
import { parseDateOnly } from '../utils/format'
import { normalizeStartTime, formatStartTime, compareDayTimeId, visibleColumnKeys } from './reportColumns'

// One list, owned by reportTimeEntriesUrl.js (which decides the fetch URL).
export const MATTER_TIME_REPORTS = MATTER_FULL_HISTORY_REPORTS
export const isMatterTimeReport = (reportId) => MATTER_TIME_REPORTS.includes(reportId)

export const UNBILLED_REPORT = 'unbilled_time_by_matter'
export const BILLED_REPORT = 'billed_time_by_matter'

// ── Billing status ─────────────────────────────────────────────────────────

export const BILLING_STATUS_ORDER = Object.freeze(['billable', 'no-charge-show', 'no-charge-hide', 'non-billable'])

const BILLING = Object.freeze({
  'billable':       { label: 'Billable',           short: 'Billable', chargeable: true },
  'no-charge-show': { label: 'No charge (shown)',  short: 'NC-show',  chargeable: false },
  'no-charge-hide': { label: 'No charge (hidden)', short: 'NC-hide',  chargeable: false },
  'non-billable':   { label: 'Non-billable',       short: 'Non-bill', chargeable: false },
})

// The DB default is 'billable', so a null/blank value means billable.
export function billingKey(bs) {
  if (bs == null) return 'billable'
  const s = String(bs).trim()
  return s === '' ? 'billable' : s
}

// An unknown value is shown raw and is NEVER chargeable — failing closed is
// the only safe direction on a document that states what a client owes.
export function billingInfo(bs) {
  const key = billingKey(bs)
  const known = BILLING[key]
  if (known) return { key, ...known }
  return { key, label: key, short: key, chargeable: false }
}

// Chip colours for a billing status.  Moved here from MatterDetail.js so the
// matter page and the Reports page colour the same status the same way.
export function billingStatusColor(bs) {
  switch (billingKey(bs)) {
    case 'billable': return 'bg-green-100 text-green-700'
    case 'non-billable': return 'bg-gray-100 text-gray-700'
    case 'no-charge-show': return 'bg-blue-100 text-blue-700'
    case 'no-charge-hide': return 'bg-gray-100 text-gray-500'
    default: return 'bg-gray-100 text-gray-700'
  }
}

// ── Posting / billed state ─────────────────────────────────────────────────

// `billed` and `status === 'billed'` are kept equal by the server (invariant
// TIME-ENTRY-BILLING-CONSISTENT).  Either one is enough to call a row billed,
// so an inconsistent row lands in exactly one report — never both, never none.
export function isBilledEntry(t) {
  return t?.billed === true || t?.status === 'billed'
}

export function postingInfo(t) {
  if (t?.status === 'posted') {
    return t.review_status === 'pending_review'
      ? { label: 'Posted (in review)', short: 'In review', group: 'posted' }
      : { label: 'Posted', short: 'Posted', group: 'posted' }
  }
  if (t?.status === 'unposted') return { label: 'Unposted', short: 'Unposted', group: 'unposted' }
  const raw = t?.status ? String(t.status) : '—'
  return { label: raw, short: raw, group: 'unposted' }
}

// What kind of "billed" a row is.  Derived HERE and nowhere else.
//   unbilled      not billed at all
//   invoice       on an Omnia invoice (charged iff billing status is Billable)
//   amicus_zero   billed at no charge on an Amicus $0 bill (no Omnia invoice)
//   amicus_other  billed in Amicus, no bill linked
//   no_invoice    billed, nothing on file
// `amicus_bill_id` is undefined before the #837 migration; `!= null` treats
// undefined as null, so an older API simply never reaches the Amicus branches.
export function billedKind(t) {
  if (!isBilledEntry(t)) return 'unbilled'
  if (t.invoice_id != null) return 'invoice'
  if (t.amicus_bill_id != null) return 'amicus_zero'
  if (t.amicus_id != null && String(t.amicus_id).trim() !== '') return 'amicus_other'
  return 'no_invoice'
}

const invoiceNo = (t) => (t?.invoice_number != null && String(t.invoice_number).trim() !== ''
  ? `#${String(t.invoice_number).trim()}`
  : `(invoice id ${t.invoice_id})`)

export function invoiceInfo(t) {
  const kind = billedKind(t)
  if (kind === 'invoice') {
    const n = invoiceNo(t)
    return { kind, label: n, short: n, invoiceId: t.invoice_id }
  }
  if (kind === 'amicus_zero') {
    const n = t.amicus_bill_number != null && String(t.amicus_bill_number).trim() !== ''
      ? ` #${String(t.amicus_bill_number).trim()}`
      : ''
    return { kind, label: `No charge (Amicus $0 bill${n})`, short: `N/C $0 bill${n}`, invoiceId: null }
  }
  if (kind === 'amicus_other') {
    return { kind, label: 'No charge (Amicus — no Omnia invoice)', short: 'N/C Amicus', invoiceId: null }
  }
  if (kind === 'no_invoice') {
    return { kind, label: 'Billed — no invoice on file', short: 'No invoice', invoiceId: null }
  }
  return { kind, label: '', short: '', invoiceId: null }
}

// Charged = on an Omnia invoice AND marked Billable.  Nothing else ever
// counts as money charged to the client.
export function isChargedBilledEntry(t) {
  return isBilledEntry(t) && t.invoice_id != null && billingKey(t.billing_status) === 'billable'
}

// The tooltip on a billed row's "billed" chip.  Keyed on billedKind and
// amicus_bill_id — never on invoice_id alone, which cannot tell an Amicus
// invoice (can't be voided) from a native one (void unlocks it).
export function billedTooltip(t) {
  const kind = billedKind(t)
  const info = invoiceInfo(t)
  if (kind === 'invoice') {
    return t.amicus_bill_id != null
      ? `Billed in Amicus on invoice ${info.label} — read-only history; this invoice can't be voided`
      : `Billed on invoice ${info.label} — void the invoice to unlock`
  }
  if (kind === 'amicus_zero') {
    const n = t.amicus_bill_number != null && String(t.amicus_bill_number).trim() !== ''
      ? ` #${String(t.amicus_bill_number).trim()}` : ''
    return `Billed in Amicus at no charge ($0 bill${n}) — read-only history; not on any Omnia invoice`
  }
  return info.label
}

// ── Integer arithmetic ─────────────────────────────────────────────────────

// Billable hours on the firm's 0.1 grid, ceil, 0.1 minimum on any work —
// the same rule as utils/format roundBillable, done in integers.
export function hoursTenths(h) {
  const n = Number(h)
  if (!Number.isFinite(n) || n <= 0) return 0
  return Math.max(1, Math.ceil(Math.round(n * 1000) / 100))
}
export function rateCents(r) {
  const n = Number(r)
  return Number.isFinite(n) ? Math.round(n * 100) : 0
}
export function valueCents(tenths, rc) {
  return Math.round((tenths * rc) / 10)
}

export function chargedCents(t) {
  return isChargedBilledEntry(t) ? valueCents(hoursTenths(t.hours), rateCents(t.rate)) : 0
}

export const fmtMoney = (cents) => {
  const c = Number(cents) || 0
  const n = Math.abs(c) / 100
  return `${c < 0 ? '-' : ''}$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
}
export const fmtHours = (tenths) =>
  ((Number(tenths) || 0) / 10).toLocaleString('en-US', { minimumFractionDigits: 1, maximumFractionDigits: 1 })

// ── Dates ──────────────────────────────────────────────────────────────────

const pad2 = (n) => String(n).padStart(2, '0')

// A pg DATE arrives as "YYYY-MM-DD" or "YYYY-MM-DDT00:00:00.000Z".  Read the
// calendar date straight off the string — never through `new Date()`, which
// would shift it a day for anyone west (or, in Taipei, east) of UTC.
export function isoDate(d) {
  if (d == null || d === '') return ''
  if (typeof d === 'string') {
    const m = d.match(/^(\d{4})-(\d{2})-(\d{2})(?:$|T00:00:00(?:\.0+)?Z$)/)
    if (m) return `${m[1]}-${m[2]}-${m[3]}`
  }
  const dt = parseDateOnly(d)
  if (!dt || Number.isNaN(dt.getTime())) return ''
  return `${dt.getFullYear()}-${pad2(dt.getMonth() + 1)}-${pad2(dt.getDate())}`
}
const usDate = (iso) => {
  const m = String(iso || '').match(/^(\d{4})-(\d{2})-(\d{2})$/)
  return m ? `${m[2]}/${m[3]}/${m[1]}` : '—'
}

// ── Rows ───────────────────────────────────────────────────────────────────

function scope(entries, { matterId, attorneyId } = {}) {
  const mid = matterId == null || matterId === '' ? null : String(matterId)
  const aid = attorneyId == null || attorneyId === '' ? '' : String(attorneyId)
  const onMatter = (Array.isArray(entries) ? entries : []).filter(t => mid == null || String(t?.matter_id) === mid)
  const shown = onMatter.filter(t => !aid || String(t?.user_id) === aid)
  return { onMatter, shown }
}

// Date order; within a day by start time (entries with none last), then id.
const byDateTimeId = (a, b) => compareDayTimeId(a, b, { dateOf: r => r.date, timeOf: r => r.time, idOf: r => r.id })

export function buildUnbilledRows(entries, opts = {}) {
  const { shown } = scope(entries, opts)
  return shown.filter(t => !isBilledEntry(t)).map(t => {
    const b = billingInfo(t.billing_status)
    const p = postingInfo(t)
    const tenths = hoursTenths(t.hours)
    const rc = rateCents(t.rate)
    const std = valueCents(tenths, rc)
    return {
      id: Number(t.id),
      date: isoDate(t.date),
      // 'HH:MM', or '' (blank Time) for an entry saved without a start time.
      time: normalizeStartTime(t.start_time),
      attorney: t.attorney_name || '—',
      description: t.description || '',
      posting: p.label,
      billing: b.label,
      hours: tenths / 10,
      rate: rc / 100,
      std: std / 100,
      value: b.chargeable ? std / 100 : null,
      _billing: b.key,
      _billingShort: b.short,
      _postingShort: p.short,
      _group: p.group,
      _chargeable: b.chargeable,
      _tenths: tenths,
      _rateCents: rc,
      _stdCents: std,
      _valueCents: b.chargeable ? std : null,
    }
  }).sort(byDateTimeId)
}

export function buildBilledRows(entries, opts = {}) {
  const { shown } = scope(entries, opts)
  return shown.filter(isBilledEntry).map(t => {
    const b = billingInfo(t.billing_status)
    const inv = invoiceInfo(t)
    const tenths = hoursTenths(t.hours)
    const rc = rateCents(t.rate)
    const charged = isChargedBilledEntry(t) ? valueCents(tenths, rc) : 0
    return {
      id: Number(t.id),
      date: isoDate(t.date),
      time: normalizeStartTime(t.start_time),
      attorney: t.attorney_name || '—',
      description: t.description || '',
      billing: b.label,
      hours: tenths / 10,
      rate: rc / 100,
      billed: charged / 100,
      invoice: inv.label,
      _billing: b.key,
      _billingShort: b.short,
      _kind: inv.kind,
      _invoiceShort: inv.short,
      _invoiceId: inv.invoiceId,
      _charged: isChargedBilledEntry(t),
      _tenths: tenths,
      _rateCents: rc,
      _chargedCents: charged,
    }
  }).sort(byDateTimeId)
}

export function buildRows(reportId, entries, opts = {}) {
  return reportId === BILLED_REPORT ? buildBilledRows(entries, opts) : buildUnbilledRows(entries, opts)
}

// ── Footers ────────────────────────────────────────────────────────────────

const entriesWord = (n) => `${n} ${n === 1 ? 'entry' : 'entries'}`
const sum = (rows, f) => rows.reduce((s, r) => s + (f(r) || 0), 0)

function unbilledLine(key, name, rows, { billable = true } = {}) {
  return {
    key,
    name,
    label: `${name} (${entriesWord(rows.length)})`,
    entries: rows.length,
    hoursTenths: sum(rows, r => r._tenths),
    stdCents: sum(rows, r => r._stdCents),
    billCents: billable ? sum(rows, r => r._valueCents) : null,
  }
}

// Posted, Unposted, one line per billing status present, TOTAL UNBILLED.
// Every line carries entries, hours, standard value and billable value.
// A no-charge / non-billable line's billable value is null (printed "—"):
// that time is not chargeable, so there is no billable value to subtotal.
export function unbilledFooter(rows) {
  const rs = Array.isArray(rows) ? rows : []
  const out = [
    unbilledLine('posted', 'Posted', rs.filter(r => r._group === 'posted')),
    unbilledLine('unposted', 'Unposted', rs.filter(r => r._group !== 'posted')),
  ]
  for (const key of BILLING_STATUS_ORDER) {
    const group = rs.filter(r => r._billing === key)
    if (group.length) out.push(unbilledLine(`billing:${key}`, BILLING[key].label, group, { billable: key === 'billable' }))
  }
  const unknown = rs.filter(r => !BILLING_STATUS_ORDER.includes(r._billing))
  if (unknown.length) {
    const raws = [...new Set(unknown.map(r => r._billing))].sort()
    out.push(unbilledLine('billing:other', `Other (${raws.join(', ')})`, unknown, { billable: false }))
  }
  out.push({ ...unbilledLine('total', 'TOTAL UNBILLED', rs), total: true })
  return out
}

function billedLine(key, name, rows) {
  return {
    key,
    name,
    label: `${name} (${entriesWord(rows.length)})`,
    entries: rows.length,
    hoursTenths: sum(rows, r => r._tenths),
    chargedCents: sum(rows, r => r._chargedCents),
  }
}

export function billedFooter(rows) {
  const rs = Array.isArray(rows) ? rows : []
  const out = [billedLine('charged', 'Charged on invoices', rs.filter(r => r._charged))]
  const ncInvoice = rs.filter(r => r._kind === 'invoice' && !r._charged)
  const ncZero = rs.filter(r => r._kind === 'amicus_zero')
  const noInvoice = rs.filter(r => r._kind === 'amicus_other' || r._kind === 'no_invoice')
  if (ncInvoice.length) out.push(billedLine('nc_invoice', 'No charge — on an invoice', ncInvoice))
  if (ncZero.length) out.push(billedLine('nc_amicus_zero', 'No charge — Amicus $0 bill', ncZero))
  if (noInvoice.length) out.push(billedLine('no_invoice', 'Billed — no invoice on file', noInvoice))
  out.push({ ...billedLine('total', 'TOTAL BILLED', rs), total: true })
  return out
}

export function footerFor(reportId, rows) {
  return reportId === BILLED_REPORT ? billedFooter(rows) : unbilledFooter(rows)
}

// Printed under the table on screen, in the PDF and in Excel.  Excel leaves
// the cell EMPTY where the screen and PDF print "—" (a dash in a numeric
// column breaks SUM()), so its note says "blank" instead.
export function notesFor(reportId, rows, target = 'screen') {
  const rs = Array.isArray(rows) ? rows : []
  if (reportId === BILLED_REPORT) {
    const notes = ['Billed amounts are line values before any invoice-level discount or credit.']
    if (rs.some(r => r._kind === 'amicus_zero')) {
      notes.push('No charge (Amicus $0 bill): time billed at no charge on an Amicus $0 bill. It is not on any Omnia invoice.')
    }
    return notes
  }
  return [
    'Standard value is hours × rate for every entry, including no-charge and non-billable time. It measures the work recorded; it is not an amount billed or owed.',
    target === 'excel'
      ? 'Billable value counts only entries marked Billable. A blank Billable value means the entry is not chargeable to the client.'
      : 'Billable value counts only entries marked Billable. — means the entry is not chargeable to the client.',
  ]
}

// ── Context + empty states ─────────────────────────────────────────────────

export function matterTimeContext(entries, opts = {}) {
  const { onMatter, shown } = scope(entries, opts)
  const billedOnMatter = onMatter.filter(isBilledEntry).length
  const billedShown = shown.filter(isBilledEntry).length
  return {
    totalOnMatter: onMatter.length,
    billedOnMatter,
    unbilledOnMatter: onMatter.length - billedOnMatter,
    totalShown: shown.length,
    billedShown,
    unbilledShown: shown.length - billedShown,
    attorneyFiltered: !(opts.attorneyId == null || opts.attorneyId === ''),
  }
}

// "Doe, Jane v. City of Springfield, et al.," — the trailing punctuation
// is part of how the firm wrote the caption, but it reads as a typo in a
// sentence.  Sentences add their own full stop.
export const sentenceTitle = (title) => String(title || 'this matter').trim().replace(/[\s,;:.]+$/, '') || 'this matter'
export const displayTitle = (title) => String(title || '').trim().replace(/[\s,;:]+$/, '')

// Never mentions dates: both reports ignore the From / To range.
export function emptyStateFor(reportId, ctx = {}, { matterTitle, attorneyName, ownOnly } = {}) {
  const title = sentenceTitle(matterTitle)
  const billed = reportId === BILLED_REPORT
  const kind = billed ? 'billed' : 'unbilled'
  const lines = []
  let heading
  const total = ctx.totalOnMatter || 0
  const onMatter = billed ? (ctx.billedOnMatter || 0) : (ctx.unbilledOnMatter || 0)
  if (total === 0) {
    heading = `No time has been recorded on ${title}.`
  } else if (ctx.attorneyFiltered && onMatter > 0) {
    heading = `No ${kind} time by ${attorneyName || 'this attorney'} on ${title}.`
    lines.push(`${entriesWord(onMatter)} of ${kind} time on this matter ${onMatter === 1 ? 'is' : 'are'} by other attorneys — choose "All attorneys" to include ${onMatter === 1 ? 'it' : 'them'}.`)
  } else if (billed) {
    heading = `No billed time on ${title}.`
    lines.push(`None of the ${total} time ${total === 1 ? 'entry' : 'entries'} on this matter has been billed yet — see Unbilled Time by Matter.`)
  } else {
    heading = `No unbilled time on ${title}.`
    lines.push(`All ${total} time ${total === 1 ? 'entry' : 'entries'} on this matter ${total === 1 ? 'has' : 'have'} been billed — see Billed Time by Matter.`)
  }
  if (ownOnly) lines.push('You can see only your own time entries on this matter.')
  return { title: heading, lines }
}

// The line under the report title on screen, in the PDF and in Excel — in
// place of the date range, which these reports do not use.
export function subtitleFor({ clientName, fileNumber, attorneyName, ownOnly } = {}) {
  const parts = []
  if (clientName) parts.push(clientName)
  if (fileNumber) parts.push(`File #${fileNumber}`)
  parts.push('All dates (full matter history)')
  if (attorneyName) parts.push(`Attorney: ${attorneyName}`)
  if (ownOnly) parts.push('Your entries only')
  return parts.join(' · ')
}

// ── Totals for the Matter Time PDF (lib/matterPDF.js) ──────────────────────

// Each bucket's `cents` is what the PDF's Amount column sums for it, so the
// TOTAL row always equals the column above it:
//   unposted  face value of every entry (as on the matter page's UNPOSTED rows)
//   posted    T = every posted, unbilled hour; WIP = the BILLABLE ones only.
//             wipT / cents count only entries marked Billable — the same rule
//             as the matter page's WIP tile and the server's
//             posted_wip_amount.  A posted no-charge or non-billable entry is
//             not work in progress and not invoiceable (#837 round 2: the
//             repair posts ~11,000 Amicus no-charge entries), so it adds hours
//             but no money.
//   billed    what was CHARGED — Σ chargedCents — so no-charge time billed at
//             $0 never prints at its face value.
// billingKey() treats null/blank as Billable (the DB default) and an unknown
// value as NOT billable, failing closed.
export function matterTimeTotals(entries) {
  const out = {
    hoursT: 0,
    unposted: { T: 0, cents: 0 },
    posted: { T: 0, wipT: 0, cents: 0 },
    billed: { T: 0, cents: 0 },
  }
  for (const t of Array.isArray(entries) ? entries : []) {
    const tenths = hoursTenths(t.hours)
    out.hoursT += tenths
    if (isBilledEntry(t)) {
      out.billed.T += tenths
      out.billed.cents += chargedCents(t)
    } else if (t.status === 'posted') {
      out.posted.T += tenths
      if (isPostedWipEntry(t)) {
        out.posted.wipT += tenths
        out.posted.cents += valueCents(tenths, rateCents(t.rate))
      }
    } else {
      out.unposted.T += tenths
      out.unposted.cents += valueCents(tenths, rateCents(t.rate))
    }
  }
  return out
}

// Posted, not billed, and marked Billable: work in progress.
export function isPostedWipEntry(t) {
  return !isBilledEntry(t) && t?.status === 'posted' && billingKey(t.billing_status) === 'billable'
}

// Imported Amicus history that Omnia never puts on an invoice — the exact
// mirror of the server's rule (api/src/lib/amicusBillingDisposition.js
// isAmicusHistoryNotInvoiceable / amicusNotInvoiceableSql, which every
// invoice-creating query applies): an Amicus row that Amicus billed
// (amicus_bill_id set) or that is not marked Billable.  The #837 repair POSTS
// ~11,000 never-billed no-charge and cancelled Amicus entries so they stay
// visible; they are not work in progress and must never be offered for
// invoicing (Cal. Rule of Prof. Conduct 1.5; B&P §§ 6147-6148).
//
// Deliberately the RAW comparison the server makes (a null billing status is
// NOT 'billable' here), not billingKey(): the point is to agree with the
// server row for row, so a picker never offers a line the server would
// silently leave off the invoice.  Native Omnia rows (no amicus_id) are never
// affected.
export function isAmicusHistoryNotInvoiceable(t) {
  if (!t || t.amicus_id == null) return false
  return t.amicus_bill_id != null || t.billing_status !== 'billable'
}

// What the single-matter invoice generator may be offered: posted, not
// billed, and not imported Amicus history.  (Native no-charge rows stay
// selectable — the firm may still print them at $0.)
export function isInvoiceablePostedEntry(t) {
  return !isBilledEntry(t) && t?.status === 'posted' && !isAmicusHistoryNotInvoiceable(t)
}

// ── Columns + cells: the ONE grid for screen, PDF, Excel and print ─────────

const UNBILLED_COLUMNS = Object.freeze([
  { key: 'date',        header: 'Date',           pdfHeader: 'DATE',        align: 'left' },
  { key: 'time',        header: 'Time',           pdfHeader: 'TIME',        align: 'left' },
  { key: 'attorney',    header: 'Attorney',       pdfHeader: 'ATTY',        align: 'left', flex: true },
  { key: 'description', header: 'Description',    pdfHeader: 'DESCRIPTION', align: 'left', flex: true },
  { key: 'posting',     header: 'Status',         pdfHeader: 'STATUS',      align: 'center' },
  { key: 'billing',     header: 'Billing',        pdfHeader: 'BILLING',     align: 'center' },
  { key: 'hours',       header: 'Hours',          pdfHeader: 'HRS',         align: 'right', hours: true },
  { key: 'rate',        header: 'Rate',           pdfHeader: 'RATE',        align: 'right', money: true },
  { key: 'std',         header: 'Standard value', pdfHeader: 'STD VALUE',   align: 'right', money: true, total: true },
  { key: 'value',       header: 'Billable value', pdfHeader: 'BILL VALUE',  align: 'right', money: true, total: true },
])

const BILLED_COLUMNS = Object.freeze([
  { key: 'date',        header: 'Date',        pdfHeader: 'DATE',        align: 'left' },
  { key: 'time',        header: 'Time',        pdfHeader: 'TIME',        align: 'left' },
  { key: 'attorney',    header: 'Attorney',    pdfHeader: 'ATTY',        align: 'left', flex: true },
  { key: 'description', header: 'Description', pdfHeader: 'DESCRIPTION', align: 'left', flex: true },
  { key: 'billing',     header: 'Billing',     pdfHeader: 'BILLING',     align: 'center' },
  { key: 'hours',       header: 'Hours',       pdfHeader: 'HRS',         align: 'right', hours: true },
  { key: 'rate',        header: 'Rate',        pdfHeader: 'RATE',        align: 'right', money: true },
  { key: 'billed',      header: 'Billed',      pdfHeader: 'BILLED',      align: 'right', money: true, total: true },
  { key: 'invoice',     header: 'Invoice',     pdfHeader: 'INVOICE',     align: 'left' },
])

export function columnsFor(reportId) {
  return reportId === BILLED_REPORT ? BILLED_COLUMNS : UNBILLED_COLUMNS
}

// The columns a person chose to show (the Columns chooser, lib/reportColumns),
// in report order.  Every output — screen, PDF, Excel, print — draws these.
export function visibleColumnsFor(reportId, hidden) {
  const cols = columnsFor(reportId)
  const keys = visibleColumnKeys(cols.map(c => c.key), hidden)
  return cols.filter(c => keys.includes(c.key))
}

const centsForKey = (row, key) => {
  if (key === 'rate') return row._rateCents
  if (key === 'std') return row._stdCents
  if (key === 'value') return row._valueCents
  if (key === 'billed') return row._chargedCents
  return null
}

// target: 'screen' | 'pdf' | 'excel'.  Excel gets numbers for numbers (money
// to 2 dp, hours to 1 dp), ISO text for dates, and an EMPTY cell where the
// screen and PDF print "—" (a dash in a numeric column breaks SUM()).
export function formatCell(row, key, target = 'screen') {
  const xl = target === 'excel'
  const pdf = target === 'pdf'
  switch (key) {
    case 'date': return xl ? (row.date || '') : usDate(row.date)
    // '10:15 AM', or blank when the entry has no start time — on every target.
    case 'time': return formatStartTime(row.time)
    case 'attorney': return row.attorney || '—'
    case 'description': return row.description || ''
    case 'posting': return pdf ? (row._postingShort || row.posting) : row.posting
    case 'billing': return pdf ? (row._billingShort || row.billing) : row.billing
    case 'hours': return xl ? Number(((row._tenths || 0) / 10).toFixed(1)) : fmtHours(row._tenths)
    case 'invoice': return pdf ? (row._invoiceShort || row.invoice) : row.invoice
    case 'rate':
    case 'std':
    case 'value':
    case 'billed': {
      const c = centsForKey(row, key)
      if (c == null) return xl ? '' : '—'
      return xl ? Number((c / 100).toFixed(2)) : fmtMoney(c)
    }
    default: return row[key] == null ? '' : String(row[key])
  }
}

// Numeric / date-aware sort keys for the screen table.  The footer is
// computed from the rows, not their order, so sorting never moves a total.
export function sortValue(row, key) {
  switch (key) {
    case 'date': return row.date || ''
    // null sorts last either way (useTableSort): entries without a time.
    case 'time': return row.time || null
    case 'hours': return row._tenths || 0
    case 'rate': case 'std': case 'value': case 'billed': {
      const c = centsForKey(row, key)
      return c == null ? -1 : c
    }
    default: return String(row[key] ?? '').toLowerCase()
  }
}

const lineCents = (line, key) => {
  if (key === 'std') return line.stdCents
  if (key === 'value') return line.billCents
  if (key === 'billed') return line.chargedCents
  return undefined
}

// Where a footer line's label goes among the columns shown.  `span` is how
// many leading columns hold no total (hours or a totalled money column):
//   span > 0     screen: one cell spanning them; PDF: the label sits in
//                DESCRIPTION when that is among them (else the last of them)
//                and is drawn across all of them; Excel: column A.
//   span === 0   the first column shown holds a total, so the label shares
//                that cell, above its value (screen, PDF), or takes a row of
//                its own above the totals (Excel).  Never dropped, and a
//                total never sits under the wrong heading.
// With no total column shown, span is every column: the label (which names
// the line and counts its entries) still prints.
export function footerLabelSpan(cols) {
  const first = cols.findIndex(c => c.hours || c.total)
  return first === -1 ? cols.length : first
}
export function footerLabelIndex(cols) {
  const span = footerLabelSpan(cols)
  if (span === 0) return 0
  const d = cols.findIndex(c => c.key === 'description')
  return d >= 0 && d < span ? d : span - 1
}

// The ONE mapping from a footer line to cells, over the columns shown
// (`cols`, default every column).
//   pdf:    array (one string per column); label at footerLabelIndex
//   excel:  array (one cell per column); label in column A — or, when column
//           A holds a total, no label here (the caller puts it on a row above)
//   screen: [{ key, colSpan, text, align, label? }] — the label spans every
//           column before the first total; then hours, rate (blank) and the
//           money columns.  A cell with `label` prints it above its text.
// Money appears only in the totalled money columns; RATE stays blank (a sum
// of rates means nothing).  A hidden column takes its total with it.
export function footerCells(reportId, line, target = 'screen', cols = columnsFor(reportId)) {
  const cellFor = (c) => {
    if (c.hours) return target === 'excel' ? Number(((line.hoursTenths || 0) / 10).toFixed(1)) : fmtHours(line.hoursTenths)
    if (c.total) {
      const cents = lineCents(line, c.key)
      if (cents == null) return target === 'excel' ? '' : '—'
      return target === 'excel' ? Number((cents / 100).toFixed(2)) : fmtMoney(cents)
    }
    return ''
  }
  const span = footerLabelSpan(cols)
  if (target === 'screen') {
    if (span === 0) {
      return cols.map((c, i) => ({ key: c.key, colSpan: 1, text: cellFor(c), align: c.align, ...(i === 0 ? { label: line.label } : {}) }))
    }
    const out = [{ key: 'label', colSpan: span, text: line.label, align: 'left' }]
    cols.slice(span).forEach(c => out.push({ key: c.key, colSpan: 1, text: cellFor(c), align: c.align }))
    return out
  }
  if (target === 'excel') {
    return cols.map((c, i) => (i === 0 && span > 0 ? line.label : cellFor(c)))
  }
  if (span === 0) return cols.map((c, i) => (i === 0 ? `${line.label}\n${cellFor(c)}` : cellFor(c)))
  const labelIdx = footerLabelIndex(cols)
  return cols.map((c, i) => (i === labelIdx ? line.label : cellFor(c)))
}
